import { AutosaveStore } from 'stores/AutosaveStore';
import { baseballLogicService, ICellsSelection, SVAppDataGridRowManager } from '@trackman/baseball-shared-components';
import { PlayRow } from 'models/PlayEditRow/PlayRow';
import { DataKey } from 'enums/DataKey';
import { IHighlightDisplayConfig } from '@trackman/baseball-shared-components/build/services/DataGrid/types/IHighlightDisplayConfig';
import { ChangeTypes, UpdateChangeKeys } from 'enums/autosave/ChangeKeys';
import { EditRowDataKey } from 'enums/EditRowDataKey';
import { SecondSourceBoxManager } from './SecondSourceBoxManager';
import { BoxScoreManager } from './BoxScoreManager';
import { IApiSessionTeam } from '../interfaces/session/ISession';
import { SessionVerificationStore } from 'stores/SessionVerificationStore';
import { PlayerFilter } from 'stores/FilterStore';
export class PlaysGridRowManager extends SVAppDataGridRowManager<PlayRow> {
  isDisplayingWarmups = true;

  boxScoreManager: BoxScoreManager = new BoxScoreManager(this);

  secondSourceBoxManager: SecondSourceBoxManager = new SecondSourceBoxManager(this);

  autosaveStore: AutosaveStore;

  sessionStore: SessionVerificationStore;

  isAnyChange = false;

  private sessionId: string;

  constructor(autosaveStore: AutosaveStore, sessionId: string, sessionStore: SessionVerificationStore, rowHeight?: number) {
    super(rowHeight);
    this.autosaveStore = autosaveStore;
    this.sessionId = sessionId;
    this.sessionStore = sessionStore;
  }

  defaultRows: PlayRow[] = [];

  filters: PlayerFilter[] = [];

  setDefaultRows = (rows: PlayRow[]) => {
    this.defaultRows = rows;
  };

  applyFilters = (filters: PlayerFilter[]) => {
    if (filters.length === 0) {
      this.filters = [];
      this.setRows(this.defaultRows);
      this.updateGrid();
      return;
    }

    this.filters = filters;

    const rows = this.defaultRows.filter((row) => {
      return filters.some((filter) => {
        if (filter.position === 'pitcher' && row.pitcher.id === filter.value) {
          return true;
        }
        if (filter.position === 'catcher' && row.catcher.id === filter.value) {
          return true;
        }
        if (filter.position === 'batter' && row.batter.id === filter.value) {
          return true;
        }
        return false;
      });
    });

    this.setRows(rows);
    this.updateGrid();
  };

  setupFirstPlaysOfInning = (rows: PlayRow[]): void => {
    rows.forEach((row) => (row.isFirstPlayInInning = false));
    const innings = rows
      .filter((row) => !row.isDeleted)
      .map((row) => ({ inningNumber: row.inning.inningNumber, isTopInning: row.inning.isTopInning }));

    const maxInningNumber = Math.max(...innings.map((inning) => inning.inningNumber));
    const firstInningRows = [];

    if (this.isDisplayingWarmups) {
      for (let i = 1; i <= maxInningNumber; i++) {
        firstInningRows.push(rows.find((row) => row.inning.inningNumber === i && row.inning.isTopInning === true && !row.isDeleted));
        firstInningRows.push(rows.find((row) => row.inning.inningNumber === i && row.inning.isTopInning === false && !row.isDeleted));
      }
    } else {
      for (let i = 1; i <= maxInningNumber; i++) {
        firstInningRows.push(
          rows.find((row) => row.inning.inningNumber === i && row.inning.isTopInning === true && !row.isWarmup && !row.isDeleted)
        );
        firstInningRows.push(
          rows.find((row) => row.inning.inningNumber === i && row.inning.isTopInning === false && !row.isWarmup && !row.isDeleted)
        );
      }
    }

    this.innings = [];

    firstInningRows.forEach((row) => {
      if (row) {
        row.isFirstPlayInInning = true;
        this.innings.push(row);
      }
    });
    this.cellCacheManager.rerenderAllCells();
  };

  setRows(rows: PlayRow[]) {
    this.setupFirstPlaysOfInning(rows);
    super.setRows(rows);
    this.boxScoreManager.invalidateTmBoxScore();
    this.recalculateComputedProperties();
    this.updateGrid();
  }

  deleteRows(fromRowId: string, numRows: number) {
    let isLivePlayDeleted = false;

    const fromIndex = this.findIndex(fromRowId);
    for (let i = fromIndex; i < fromIndex + numRows; i++) {
      this.rows[i].isDeleted = true;
      this.rows[i].isChanged = true;
      this.rows[i].isBallTrackingChanged = true;
      this.rows[i].isPlayerTrackingChanged = true;
      this.rows[i].isMarkedForDeletion = true;
      if (!this.rows[i].isWarmup) {
        isLivePlayDeleted = true;
      }
    }

    if (this.filters.length > 0) {
      const fromIndex = this.findIndex(fromRowId);
      const rowsToDelete = this.rows.slice(fromIndex, fromIndex + numRows);
      rowsToDelete.forEach((row) => {
        row.isDeleted = true;
        row.isChanged = true;
        row.isBallTrackingChanged = true;
        row.isPlayerTrackingChanged = true;
        row.isMarkedForDeletion = true;
        if (!row.isWarmup) {
          isLivePlayDeleted = true;
        }
        const defaultRowIndex = this.findDefaultIndex(row.id);
        if (defaultRowIndex !== -1) {
          this.defaultRows[defaultRowIndex].isDeleted = true;
          this.defaultRows[defaultRowIndex].isChanged = true;
          this.defaultRows[defaultRowIndex].isBallTrackingChanged = true;
          this.defaultRows[defaultRowIndex].isPlayerTrackingChanged = true;
          this.defaultRows[defaultRowIndex].isMarkedForDeletion = true;
        }
      });
    }

    this.recalculateComputedProperties();
    this.recalculateDisplayIndex();
    this.validateRows();
    isLivePlayDeleted && this.boxScoreManager.invalidateTmBoxScore();
    this.setupFirstPlaysOfInning(this.rows);
    this.cellCacheManager.rerenderAllCells();
    this.boxScoreManager.recalculateHighlights();
    this.secondSourceBoxManager.getDiscrepancies();
    this.isAnyChange = true;
  }

  findDefaultIndex(rowId: string) {
    return this.defaultRows.findIndex((x) => x.id === rowId);
  }

  insertRowAtPosition(row: PlayRow, rowId: string, insertBelow = false) {
    super.insertRowAtPosition(row, rowId, insertBelow);

    if (this.filters.length > 0) {
      const indexManipulation = insertBelow ? 1 : 0;
      this.defaultRows.splice(this.findDefaultIndex(rowId) + indexManipulation, 0, row);
    }

    this.recalculateComputedProperties();
    this.validateRows();
    this.setupFirstPlaysOfInning(this.rows);
    this.boxScoreManager.invalidateTmBoxScore();
    this.boxScoreManager.recalculateHighlights();
    this.secondSourceBoxManager.getDiscrepancies();
    this.isAnyChange = true;
  }

  updateRow(index: number, dataKey: string, newValue: any, rowId: string, autosave = true): void {
    super.updateRow(index, dataKey, newValue, rowId);

    if (autosave && rowId && rowId?.length > 0) {
      this.autosaveStore.save(this.sessionId, {
        [UpdateChangeKeys.DataKey]: dataKey,
        [UpdateChangeKeys.NewValue]: newValue,
        [UpdateChangeKeys.RowId]: rowId,
        [UpdateChangeKeys.Type]: ChangeTypes.Update,
      });

      this.isAnyChange = true;
    }

    this.recalculateComputedProperties();
    this.validateRows();
    if (!this.rows[index].isWarmup || dataKey === DataKey.IsWarmup) {
      this.boxScoreManager.invalidateTmBoxScore();
    }
    if (dataKey === DataKey.IsWarmup) {
      this.recalculateDisplayIndex();
      this.cellCacheManager.rerenderAllCells();
      this.updateGrid();
    }
    this.setupFirstPlaysOfInning(this.rows);
    this.boxScoreManager.recalculateHighlights();
    this.secondSourceBoxManager.getDiscrepancies();
  }

  handleTeamsChanged(newHomeTeam: IApiSessionTeam, newAwayTeam: IApiSessionTeam, isHomeTeamChanged: boolean, isAwayTeamChanged: boolean) {
    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i];
      if (row.isDeleted) {
        continue;
      }
      if (isHomeTeamChanged) {
        row.changeTeam(newHomeTeam.name, true);
      }
      if (isAwayTeamChanged) {
        row.changeTeam(newAwayTeam.name, false);
      }
    }
    this.recalculateComputedProperties();
    this.validateRows();
    this.boxScoreManager.invalidateTmBoxScore();
    this.cellCacheManager.rerenderAllCells();
    this.updateGrid();
    this.isAnyChange = true;
  }

  recalculateComputedProperties() {
    const fixedApiPlays = baseballLogicService.recalculateApiPlayComputedProperties(this.rows.map((r) => (r as PlayRow).toApiPlay().play));
    for (let i = 0; i < this.rows.length; i++) {
      const play = this.rows[i] as PlayRow;
      const { taggerBehavior } = fixedApiPlays[i];
      if (
        (taggerBehavior.pAofInning != play.pAofInning || // Usage of != instead of !== is by design
          taggerBehavior.pitchOfPA != play.pitchOfPA) &&
        this.filters.length === 0
      ) {
        play.pAofInning = taggerBehavior.pAofInning;
        play.pitchOfPA = taggerBehavior.pitchOfPA;
        play.isChanged = true;
        play.isBallTrackingChanged = true;
        this.cellCacheManager.addRowsToUpdate([i]);
      }
      if (
        (taggerBehavior.playSequenceNumber != play.playSequenceNumber || // Usage of != instead of !== is by design
          taggerBehavior.sequenceNumber != play.sequenceNumber) &&
        this.filters.length === 0
      ) {
        play.playSequenceNumber = taggerBehavior.playSequenceNumber;
        play.sequenceNumber = taggerBehavior.sequenceNumber;
        play.isChanged = true;
        play.isBallTrackingChanged = true;
        play.isPlayerTrackingChanged = true;
        this.cellCacheManager.addRowsToUpdate([i]);
      }
    }
  }

  recalculateDisplayIndex() {
    let i = 0;
    this.rows.forEach((row, idx) => {
      let dispIdx = row.displayIndex;
      if (row.isDeleted || (!this.isDisplayingWarmups && row.isWarmup)) {
        dispIdx = -1;
      } else {
        dispIdx = i;
        i++;
      }
      if (row.displayIndex !== dispIdx) {
        row.displayIndex = dispIdx;
        this.cellCacheManager.addRowsToUpdate([idx]);
      }
    });
  }

  setIsDisplayingWarmups(newVal: boolean) {
    this.isDisplayingWarmups = newVal;
    this.setupFirstPlaysOfInning(this.rows);
    this._updateSelection(undefined);
    this.recalculateDisplayIndex();
    this.boxScoreManager.recalculateHighlights();
    this.secondSourceBoxManager.getDiscrepancies();
    this.cellCacheManager.rerenderAllCells();
    this.updateGrid();
  }

  getClassNameForCell = (rowIndex: number, columnIndex: number) => {
    const row = this.rows[rowIndex];
    if (row.isMarkedForDeletion) {
      return 'deleted';
    }
    if (row.isCreated) {
      return 'created';
    }
    return '';
  };

  selectionChangeMiddleware = (_oldSelection: ICellsSelection | undefined, newSelection: ICellsSelection | undefined) => {
    if (newSelection) {
      const { isError, message } = this.getSelectionError(newSelection);
      newSelection.isSelectionError = isError;
      newSelection.selectionErrorText = message;
    }
    return newSelection;
  };

  getSelectionError(selection: ICellsSelection) {
    const selectedRows = this.rows.slice(selection.fromRowIndex, selection.toRowIndex + 1);
    const innings = selectedRows.filter((r) => !r.isDeleted).map((r) => `${r.inning.inningNumber}-${r.inning.isTopInning}`);
    if (new Set(innings).size > 1) {
      return { isError: true, message: 'You cannot select across multiple innings' };
    }
    return { isError: false, message: '' };
  }

  getLiveRows() {
    return this.rows.filter((r) => !r.isDeleted && !r.isWarmup);
  }

  scrollToNextHighlight(currentHighlight: IHighlightDisplayConfig, isDirectionDown: boolean) {
    const highlights = this.highlightManager.highlights.filter((h) => h.groupId === currentHighlight.groupId);
    highlights.sort((a, b) => a.fromRowIndex - b.fromRowIndex);
    const currIdx = highlights.findIndex((h) => h.id === currentHighlight.id);
    let nextIdx = currIdx + (isDirectionDown ? 1 : -1);
    if (nextIdx > highlights.length - 1) {
      nextIdx = 0;
    }
    if (nextIdx < 0) {
      nextIdx = highlights.length - 1;
    }
    this.scrollToRow(highlights[nextIdx].toRowIndex);
  }

  validateRows = () => {
    super.validateRows();
    this.rows
      .filter((row) => row.status.errors.length > 0)
      .forEach((row) => {
        if (row.isWarmup) {
          row.status = {
            errors: [],
            status: '',
          };
          return;
        }
        row.status.errors.forEach((error, index) => {
          if (row.ignoredColumns.includes(error.dataKey)) {
            row.status.errors.splice(index, 1);
          }
        });
      });
  };

  ignoreErrors = (dataKey: DataKey | EditRowDataKey, fromRowIndex: number, numRows: number) => {
    for (let i = fromRowIndex; i < fromRowIndex + numRows; i++) {
      this.rows[i].ignoredColumns.push(dataKey);
      const errorIndex = this.rows[i].status.errors.findIndex((error) => error.dataKey === dataKey);
      this.rows[i].status.errors.splice(errorIndex, 1);
    }
    this.cellCacheManager.rerenderAllCells();
    this.updateGrid();
  };
}
